import { DownOutlined, PlusCircleOutlined } from '@ant-design/icons';
import { Form, Input, InputNumber, message, Select } from 'antd';
import { FormInstance } from 'antd/es/form/hooks/useForm';
import classnames from 'classnames';
import { get } from 'lodash-es';
import { observer } from 'mobx-react-lite';
import React, { FC, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import * as uuid from 'uuid';
import AddCircleIcon from '@/assets/images/icons/add-circle.react.svg?react';
import CloseCircleIcon from '@/assets/images/icons/close-circle.react.svg?react';
import i18n from '@/i18n';
import { RecordType } from '@/presentation/pages/setting/app-deliver-config/components/configuration-content';
import { renderModal } from '@/presentation/pages/setting/app-deliver-config/components/sub-config-item';
import { CUSTOMIZE_CONFIG } from '@/presentation/pages/setting/app-deliver-config/constant';
import { customizeDataTransform, isLong } from '@/presentation/pages/setting/app-deliver-config/utils';
import { useStores } from '@/presentation/store';
import styles from './customize-item.module.scss';

interface IProps {
  config: RecordType;
  setConfigs: React.Dispatch<React.SetStateAction<any[]>>
  configs: RecordType[]
  form?: FormInstance;
  fatherNameOption?: { label: string, value: string }[];
  typeDisabled?: boolean;
  fatherDel?: () => void;
  fatherAdd?: () => void;
  fatherNameChange?: (key: string) => void;
  fatherNameValue?: string;
  childName?: RecordType;
  readonly?: boolean;
  fatherAddVisible?: boolean;
}

export enum  IType {
  FLOAT = 'float',
  INT = 'int',
  BOOL = 'bool',
  STRING = 'string',
  OBJECT = 'object',
  ARRAY = 'array',
}

interface IData {
  key: string;
  name: string;
  value?: string | boolean | number;
  type: IType;
  children?: IData[]
}
interface IUpdateData {
  key?: string;
  name?: string;
  value?: string | boolean | number;
  type?: IType;
  children?: IData[]
}

interface IJsonItem {
  data: IData;
  setChildren: (children: IData[]) => void;
  childrenList: IData[];
  isAddAdjacent: boolean;
  fatherType: IType;
  typeDisabled?: boolean;
  fatherDel?: () => void;
  fatherAdd?: () => void;
  fatherNameOption?: { label: string, value: string }[];
  fatherNameChange?: (key: string) => void;
  fatherNameValue?: string;
  childName?: RecordType;
  readonly?: boolean;
  fatherAddVisible?: boolean;
  configFieldInfo: Record<string, any>;
}
const arrayOrObject = [IType.OBJECT, IType.ARRAY];

const numberTypes = [IType.INT, IType.FLOAT];

const typeOption = [
  { value: IType.STRING, label: IType.STRING },
  { value: IType.FLOAT, label: IType.FLOAT },
  { value: IType.INT, label: IType.INT },
  { value: IType.BOOL, label: IType.BOOL },
  { value: IType.OBJECT, label: IType.OBJECT },
  { value: IType.ARRAY, label: IType.ARRAY },
];
const FormItem = Form.Item;
const boolOption = [
  { value: true, label: 'true' },
  { value: false, label: 'false' },
];
// json对象转自定义数据
const  jsonTransformCustomize = (values: any, name: string, configFieldInfo?: Record<string, any>): IData => {
  const res: any = {};
  if (Array.isArray(values)) {
    res.key = uuid.v4();
    res.name = name;
    res.type = customizeDataTransform(values);
    res.value = arrayOrObject.includes(res.type) ? undefined : values;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    res.children = values.map((item: any) => jsonTransformCustomize(item, 'item', configFieldInfo));
  } else if (values instanceof Object) {
    const keys = Object.keys(values);
    res.key = uuid.v4();
    res.name = name;
    res.type = customizeDataTransform(values);
    res.value = undefined;
    res.children = keys.map((item: any) => jsonTransformCustomize(values[item], item, configFieldInfo)) || [];
  } else {
    // 读取configFieldInfo定义的类型
    const configFieldInfoType = configFieldInfo ? isLong(get(configFieldInfo, `${name}.type`)) : undefined;
    res.key = uuid.v4();
    res.name = name;
    res.type = configFieldInfoType ? configFieldInfoType : customizeDataTransform(values);
    res.value = values;
  }

  return res;
};

export const CustomizeData: FC<IProps> = observer((props: IProps) => {
  const { config,
    configs,
    setConfigs,
    form,
    typeDisabled,
    fatherDel,
    fatherAdd,
    fatherNameOption,
    fatherNameValue,
    childName,
    readonly,
    fatherAddVisible,
    fatherNameChange } = props;
  const rootName = Object.keys(config)?.[0] || CUSTOMIZE_CONFIG.name;

  const {
    configs: operation = {},
  } = useStores().ConfigStore;
  const { configFieldInfo = {} } = operation;
  // 自定义不读取远程类型
  const customizeData = jsonTransformCustomize(
    config[rootName], rootName,
    rootName === CUSTOMIZE_CONFIG.name ? undefined : configFieldInfo,
  );
  const [data, setData] = useState<IData>(customizeData);
  useEffect(() => {
    const newConfig = customizeTransformJson({}, data);
    setConfigs(configs.map((item) => {
      if (item[rootName]) {
        return newConfig;
      }
      return item;
    }));
  }, [data]);

  // 自定义数据转json对象
  const customizeTransformJson = (value: RecordType, data: IData) => {
    if (data.type === IType.OBJECT) {
      const emptyObject = {};
      data.children?.forEach((item) => {
        customizeTransformJson(emptyObject, item);
      });
      // eslint-disable-next-line no-param-reassign
      value[data.name] = emptyObject;
    } else if (data.type === IType.ARRAY) {
      const list: any[] = [];
      data.children?.forEach((item) => {
        list.push(Object.values(customizeTransformJson({}, item))[0]);
      });
      // eslint-disable-next-line no-param-reassign
      value[data.name] = list;
    } else {
      // eslint-disable-next-line no-param-reassign
      value[data.name] = data.value;
    }
    return value;
  };

  const setChildren = (children: IData[]) => {
    setData(data => ({
      ...data,
      children,
    }));
  };

  return <div className={styles.customize}>
        <Form component={false} form={form}>
        {data?.children?.map((item, index) => <JsonItem
          key={item.key}
          childrenList={data?.children || []}
          data={item}
          readonly={readonly}
          fatherType={IType.OBJECT}
          setChildren={setChildren}
          typeDisabled={typeDisabled}
          fatherDel={fatherDel}
          fatherAdd={fatherAdd}
          fatherNameOption={fatherNameOption}
          fatherNameValue={fatherNameValue}
          fatherNameChange={fatherNameChange}
          childName={childName}
          fatherAddVisible={fatherAddVisible}
          isAddAdjacent={(index + 1) === data.children?.length}
          configFieldInfo={rootName !== CUSTOMIZE_CONFIG.name ? configFieldInfo : undefined}
        />)}
      {data?.children?.length || readonly ? undefined  : <PlusButton onClick={() => setData(data => ({
        ...data,
        children: [...data.children || [], {
          name: '',
          key: uuid.v4(),
          value: undefined,
          type: IType.STRING,
          children: [],
        }],
      }))}/>}
        </Form>
  </div>;
});

const JsonItem: FC<IJsonItem> = ({ data,
  setChildren,
  childrenList,
  isAddAdjacent,
  fatherType,
  typeDisabled,
  fatherDel,
  fatherNameOption,
  fatherNameChange,
  fatherNameValue,
  childName,
  readonly,
  fatherAddVisible,
  configFieldInfo,
  fatherAdd }) => {
  const { t } = useTranslation();
  const { key, children: dataChildren } = data;
  const rules = [{ required: true, message: t('配置属性不能为空') }];
  const [visible, setVisible] = useState(true);
  const [type, setType] = useState(data.type);
  const downClick = () => {
    setVisible(visible => !visible);
  };
  useEffect(() => {
    setType(data.type);
  }, [data.type]);

  const addItem = () => {
    // 兼容非自定义
    if (fatherType === IType.ARRAY && childName && !fatherNameOption) {
      // 非自定义数据增加
      setChildren([...childrenList || [], {
        name: 'item',
        key: uuid.v4(),
        value: undefined,
        type: customizeDataTransform(childName),
        children: Object.keys(childName).map((childNameKey) => {
          // 读取configFieldInfo定义的类型
          const configFieldInfoType = configFieldInfo ? isLong(get(configFieldInfo, `${childNameKey}.type`)) : undefined;
          return {
            name: childNameKey,
            key: uuid.v4(),
            value: childName[childNameKey],
            type: configFieldInfoType ? configFieldInfoType :  customizeDataTransform(childName[childNameKey]),
          };
        }) }]);
    } else if (childName && !fatherNameOption) {
      // 判断是否添加
      const childNameKey =  Object.keys(childName).
        find(childKey => childrenList.every(childrenItem => childrenItem.name !== childKey));
      if (childNameKey) {
        // 读取configFieldInfo定义的类型
        const configFieldInfoType = configFieldInfo ? isLong(get(configFieldInfo, `${childNameKey}.type`)) : undefined;
        setChildren([...childrenList || [], {
          name: childNameKey,
          key: uuid.v4(),
          value: childName[childNameKey],
          type: configFieldInfoType ? configFieldInfoType
            : customizeDataTransform(childName[childNameKey]), children: [] }]);
      } else {
        message.warning(t('该配置项下的属性已全部添加'));
      }
    } else {
      setChildren([...childrenList || [], {
        name: fatherType === IType.ARRAY ? 'item' : '',
        key: uuid.v4(),
        value: undefined,
        type: fatherType === IType.ARRAY && childrenList.length > 0 ? childrenList[0].type : IType.STRING,
        children: [] }]);
    }
  };
  const setChildrenItem = (list: IData[]) => {
    updateChildren({ children: list });
  };

  // 删除
  const delItem = () => {
    renderModal(
      t('删除 {{name}}', { name: data.name }),
      t('确定删除此项配置属性吗？'),
      () => setChildren(childrenList.filter(item => key !== item.key)),
    );
  };

  const plusButtonClick = () => {
    updateChildren({ children: [{
      name: type === IType.ARRAY ? 'item' : '',
      key: uuid.v4(),
      value: undefined,
      type: IType.STRING, children: [] }] });
  };

  // 更新节点
  const updateChildren = (values: IUpdateData) => {
    setChildren(childrenList.map((item) => {
      if (key === item.key) {
        return { ...item, ...values };
      }
      return item;
    }));
  };

  const typeChange = (value: IType) => {
    setType(value);
    if (fatherType === IType.ARRAY) {
      setChildren(childrenList.map(item => ({ ...item, type: value, children: [], value: undefined })));
    } else {
      updateChildren({ type: value, children: [], value: undefined });
    }
    if (!visible && !arrayOrObject.includes(value)) {
      setVisible(true);
    }
  };

  const nameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    updateChildren({ name: value });
  };

  const childNameChange = (value: string) => {
    if (childName && value !== data.name) {
      if (childrenList.every(childrenItem => childrenItem.name !== value)) {
        // 读取configFieldInfo定义的类型
        const configFieldInfoType = configFieldInfo ? isLong(get(configFieldInfo, `${value}.type`)) : undefined;
        updateChildren({ name: value,
          key: uuid.v4(),
          value: childName[value],
          type: configFieldInfoType ? configFieldInfoType : customizeDataTransform(childName[value]), children: []  });
      } else {
        message.warning(t('选择配置项属性重复'));
      }
    }
  };

  const valueChange = (value: boolean | string | number | null) => {
    updateChildren({ value: value === null ? undefined : value });
  };

  return <div className={styles.customizeItem}>
    <div className={styles.jsonItem}>
      {arrayOrObject.includes(type) ? <FormItem className={styles.itemIcon}>
        <div onClick={downClick} className={classnames({ [styles.downOutlined]: true,
          [styles.rightOutlined]: !visible,
        })}><DownOutlined /></div></FormItem> : <div className={styles.downOutlined}></div>}
      {fatherNameOption
        ? <div
          className={styles.customizeName}>
          {<Select value={fatherNameValue}
                   showArrow={!readonly}
                   disabled={fatherType === IType.ARRAY || readonly}
                   options={fatherNameOption}
                   onChange={fatherNameChange} />}
        </div>
        : null}
      {childName && !fatherNameOption ? <div
        className={styles.customizeName}>
        {<Select value={data.name}
                 showArrow={!readonly}
                 disabled={fatherType === IType.ARRAY || readonly}
                 options={Object.keys(childName).map(item => ({ value: item, label: item }))}
                 onChange={childNameChange} />}
      </div> : null}
      {!childName && !fatherNameOption ? <FormItem
        rules={rules}
        initialValue={data.name}
        className={styles.customizeName}
        name={`${key}|name`}>
        <Input value={data.name} onChange={nameChange} disabled={fatherType === IType.ARRAY || readonly} />
      </FormItem> : null}

      <div
        className={classnames({
          [styles.customizeType]: true,
          [styles.flexAuto]: type !== IType.STRING && !numberTypes.includes(type) && type !== IType.BOOL,
        }) }>
        {typeDisabled
          ? <Input value={data.type} disabled={true} />
          : <Select showArrow={!readonly}
                    value={data.type} disabled={readonly} options={typeOption} onChange={typeChange} />}
      </div>

        {type === IType.STRING && (data.value === undefined || typeof(data.value) === 'string')
          ? <FormItem
            rules={rules}
            preserve={false} initialValue={data.value}
            name={`${key}|string|value`}
            className={styles.customizeValue}>
          <Input disabled={readonly} value={data.value !== undefined ? String(data.value) : undefined}
               onChange={(e: React.ChangeEvent<HTMLInputElement>) => valueChange(e.target.value)}/>
          </FormItem>
          : null}
        {(type === IType.INT) && (data.value === undefined || typeof(data.value) === 'number')
          ?          <FormItem
            rules={rules}
            preserve={false} initialValue={data.value}
            name={`${key}|number|value`}
            className={styles.customizeValue}>
          <InputNumber
            value={data.value}
            disabled={readonly}
            onChange={valueChange}
            precision={type === IType.INT ? 0 : undefined}/>
          </FormItem>
          : null}
      {type === IType.FLOAT && (data.value === undefined || typeof(data.value) === 'number')
        ?          <FormItem
          rules={rules}
          preserve={false} initialValue={data.value}
          name={`${key}|float|value`}
          className={styles.customizeValue}>
          <InputNumber
            value={data.value}
            disabled={readonly}
            onChange={valueChange}/>
        </FormItem>
        : null}
        {type === IType.BOOL && (data.value === undefined || typeof(data.value) === 'boolean')
          ? <FormItem rules={rules} preserve={false} initialValue={data.value} name={`${key}|bool|value`} className={styles.customizeValue}>
          <Select showArrow={!readonly}
                  disabled={readonly}
                  value={data.value}
                  options={boolOption}
                  onChange={valueChange}/>
          </FormItem>
          : null}
      {readonly ? null : <FormItem>
        <div className={classnames({
          [styles.customizeIcon]: true,
        })}>
      <span title={t('删除节点')} onClick={fatherDel || delItem}>
        <CloseCircleIcon/>
      </span>
        </div>
      </FormItem>}
      {readonly ? null : <FormItem>
        <div className={classnames({
          [styles.customizeIcon]: true,
        })}>
          {isAddAdjacent && fatherAddVisible !== false ? <span onClick={fatherAdd || addItem} title={t('添加相邻节点')}>
        <AddCircleIcon/>
      </span> : null}
        </div>
      </FormItem>}
    </div>
    {dataChildren?.length || !arrayOrObject.includes(type) || readonly ? undefined
      : <PlusButton onClick={plusButtonClick} />}
    {dataChildren?.length && visible
      ? dataChildren.map((item, index) => <JsonItem key={item.key}
                                                    readonly={readonly}
                                                    typeDisabled={typeDisabled}
                                                     childrenList={dataChildren || []}
                                                    fatherType={type}
                                                    childName={childName}
                                                    configFieldInfo={configFieldInfo}
                                                     setChildren={setChildrenItem}
                                                    isAddAdjacent={(index + 1) === dataChildren?.length}
                                                     data={item} />) : undefined}
  </div>;
};

const PlusButton = ({ onClick }: {onClick: () => void}) => <div title={i18n.t('添加子节点')} onClick={onClick} className={styles.pushButton}>
  <PlusCircleOutlined/>{i18n.t('添加')}</div>;
